import React, {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  useEffect,
} from "react";
import { usePortal } from "../../hooks/use-portal";
import "./tooltip.scss";

export type TooltipSide = "top" | "bottom" | "left" | "right";

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  placement?: TooltipSide;
  offset?: number;
  delay?: number;
  disabled?: boolean;
  showArrow?: boolean;
  maxWidth?: number | string;
  maxHeight?: number | string;
  className?: string;
  contentClassName?: string;
  open?: boolean;
}

interface TooltipCoords {
  top: number;
  left: number;
  resolvedPlacement: TooltipSide;
  arrowOffset?: number;
}

const VIEWPORT_PADDING = 8;

let idCounter = 0;

function getOppositePlacement(placement: TooltipSide): TooltipSide {
  const map: Record<TooltipSide, TooltipSide> = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left",
  };
  return map[placement];
}

function getCoordsForPlacement(
  trigger: DOMRect,
  bubble: DOMRect,
  placement: TooltipSide,
  offset: number,
): TooltipCoords {
  switch (placement) {
    case "top":
      return {
        top: trigger.top - bubble.height - offset,
        left: trigger.left + trigger.width / 2 - bubble.width / 2,
        resolvedPlacement: "top",
      };
    case "bottom":
      return {
        top: trigger.bottom + offset,
        left: trigger.left + trigger.width / 2 - bubble.width / 2,
        resolvedPlacement: "bottom",
      };
    case "left":
      return {
        top: trigger.top + trigger.height / 2 - bubble.height / 2,
        left: trigger.left - bubble.width - offset,
        resolvedPlacement: "left",
      };
    case "right":
      return {
        top: trigger.top + trigger.height / 2 - bubble.height / 2,
        left: trigger.right + offset,
        resolvedPlacement: "right",
      };
  }
}

function fitsInViewport(
  coords: TooltipCoords,
  bubble: DOMRect,
  viewportWidth: number,
  viewportHeight: number,
): boolean {
  return (
    coords.top >= VIEWPORT_PADDING &&
    coords.left >= VIEWPORT_PADDING &&
    coords.top + bubble.height <= viewportHeight - VIEWPORT_PADDING &&
    coords.left + bubble.width <= viewportWidth - VIEWPORT_PADDING
  );
}

function shiftToFit(
  coords: TooltipCoords,
  bubble: DOMRect,
  viewportWidth: number,
  viewportHeight: number,
): TooltipCoords {
  let { top, left } = coords;
  const { resolvedPlacement } = coords;

  if (resolvedPlacement === "top" || resolvedPlacement === "bottom") {
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, viewportWidth - bubble.width - VIEWPORT_PADDING),
    );
  } else {
    top = Math.max(
      VIEWPORT_PADDING,
      Math.min(top, viewportHeight - bubble.height - VIEWPORT_PADDING),
    );
  }

  return { top, left, resolvedPlacement };
}

function computePosition(
  triggerRect: DOMRect,
  bubbleRect: DOMRect,
  preferredPlacement: TooltipSide,
  offset: number,
): TooltipCoords {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const preferred = getCoordsForPlacement(
    triggerRect,
    bubbleRect,
    preferredPlacement,
    offset,
  );

  if (fitsInViewport(preferred, bubbleRect, viewportWidth, viewportHeight)) {
    return shiftToFit(preferred, bubbleRect, viewportWidth, viewportHeight);
  }

  const flipped = getCoordsForPlacement(
    triggerRect,
    bubbleRect,
    getOppositePlacement(preferredPlacement),
    offset,
  );

  if (fitsInViewport(flipped, bubbleRect, viewportWidth, viewportHeight)) {
    return shiftToFit(flipped, bubbleRect, viewportWidth, viewportHeight);
  }

  return shiftToFit(preferred, bubbleRect, viewportWidth, viewportHeight);
}

function mergeRefs<T>(
  ...refs: (React.Ref<T> | undefined)[]
): (value: T | null) => void {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

const Tooltip = ({
  content,
  children,
  placement = "top",
  offset = 8,
  delay = 150,
  disabled = false,
  showArrow = true,
  maxWidth = "240px",
  maxHeight,
  className,
  contentClassName,
  open,
}: TooltipProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);
  const [tooltipId] = useState(() => `tooltip-${++idCounter}`);

  const anchorRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const renderPortal = usePortal();
  const isControlled = open !== undefined;
  const isOpen = !disabled && (isControlled ? open : internalOpen);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const bubble = bubbleRef.current;
    if (!anchor || !bubble) return;

    const triggerRect = anchor.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();
    const position = computePosition(
      triggerRect,
      bubbleRect,
      placement,
      offset,
    );
    const horizontal =
      position.resolvedPlacement === "top" ||
      position.resolvedPlacement === "bottom";
    const targetOffset = horizontal
      ? triggerRect.left + triggerRect.width / 2 - position.left
      : triggerRect.top + triggerRect.height / 2 - position.top;
    const sideLength = horizontal ? bubbleRect.width : bubbleRect.height;
    // Keep the arrow aimed at the trigger after shifting the bubble into the
    // viewport, while leaving room for its border and the rounded corners.
    const inset = Math.min(14, sideLength / 2);
    setCoords({
      ...position,
      arrowOffset: Math.max(inset, Math.min(targetOffset, sideLength - inset)),
    });
  }, [placement, offset]);

  const clearShowTimeout = useCallback(() => {
    if (showTimeoutRef.current != null) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
  }, []);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current != null) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const hideTooltip = useCallback(() => {
    clearShowTimeout();
    if (!isControlled) {
      setInternalOpen(false);
    }
  }, [clearShowTimeout, isControlled]);

  const scheduleHideTooltip = useCallback(() => {
    clearShowTimeout();
    clearHideTimeout();

    if (maxHeight != null) {
      hideTimeoutRef.current = setTimeout(hideTooltip, 100);
      return;
    }

    hideTooltip();
  }, [clearHideTimeout, clearShowTimeout, hideTooltip, maxHeight]);

  const cancelScheduledHide = useCallback(() => {
    clearHideTimeout();
  }, [clearHideTimeout]);

  const showTooltip = useCallback(() => {
    if (disabled) return;
    cancelScheduledHide();
    clearShowTimeout();
    showTimeoutRef.current = setTimeout(() => {
      if (!isControlled) {
        setInternalOpen(true);
      }
    }, delay);
  }, [cancelScheduledHide, clearShowTimeout, delay, disabled, isControlled]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    updatePosition();
  }, [isOpen, content, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        hideTooltip();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [hideTooltip, isOpen]);

  useEffect(() => {
    clearShowTimeout();
    clearHideTimeout();
  }, [clearHideTimeout, clearShowTimeout]);

  const child = React.Children.only(children);
  const childRef = (
    child as React.ReactElement & { ref?: React.Ref<HTMLElement> }
  ).ref;

  const trigger = React.cloneElement(child, {
    ref: mergeRefs(anchorRef, childRef),
    "aria-describedby": isOpen ? tooltipId : undefined,
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
      child.props.onMouseEnter?.(event);
      showTooltip();
    },
    onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
      child.props.onMouseLeave?.(event);
      scheduleHideTooltip();
    },
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      child.props.onFocus?.(event);
      showTooltip();
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      child.props.onBlur?.(event);
      scheduleHideTooltip();
    },
  });

  const resolvedPlacement = coords?.resolvedPlacement ?? placement;
  const cls = "tooltip";

  const bubbleClass = [
    cls,
    `${cls}--${resolvedPlacement}`,
    showArrow ? `${cls}--with-arrow` : "",
    maxHeight != null ? `${cls}--scrollable` : "",
    isOpen ? `${cls}--visible` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const bubble = isOpen
    ? renderPortal(
        <div
          ref={bubbleRef}
          id={tooltipId}
          role="tooltip"
          className={bubbleClass}
          style={
            {
              "--tooltip-arrow-offset":
                coords?.arrowOffset != null ? `${coords.arrowOffset}px` : "50%",
              position: "fixed",
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              maxWidth,
              visibility: coords ? "visible" : "hidden",
              zIndex: 9999,
            } as React.CSSProperties
          }
          onMouseEnter={maxHeight != null ? showTooltip : undefined}
          onMouseLeave={maxHeight != null ? scheduleHideTooltip : undefined}
        >
          <div
            className={[`${cls}__content`, contentClassName]
              .filter(Boolean)
              .join(" ")}
            style={maxHeight != null ? { maxHeight } : undefined}
          >
            {content}
          </div>
        </div>,
      )
    : null;

  return (
    <>
      {trigger}
      {bubble}
    </>
  );
};

export { Tooltip };
export default Tooltip;
